/**
 * useTimelineDemand Hook
 *
 * Fetches demand data for a date range from the unified demand_forecasts table.
 * This table contains both actuals (past dates) and calibrated forecasts (future dates).
 *
 * US-PE-006: Sales Timeline - Phase 1 Data Hooks
 * Updated: Now uses unified demand forecast system (US-FORECAST-UNIFIED)
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { createDate } from '@/utils/dateUtils'
import { formatDateISO } from '@/utils/timeline-utils'

/**
 * Hook to fetch demand data for a date range
 *
 * @param {Object} params - Query parameters
 * @param {Date|string} params.startDate - Start of date range
 * @param {Date|string} params.endDate - End of date range
 * @param {string|null} params.strainId - Optional strain ID filter (null for all strains)
 * @returns {Object} {
 *   data: Array - [{ date, strainId, strainName, demandKg, isActual, recurringKg, predictedKg, calibrationFactor }],
 *   loading: boolean,
 *   error: string|null,
 *   refetch: Function
 * }
 */
export function useTimelineDemand({ startDate, endDate, strainId = null }) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchDemand = useCallback(async () => {
    if (!startDate || !endDate) {
      setData([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const start = typeof startDate === 'string' ? startDate : formatDateISO(startDate)
      const end = typeof endDate === 'string' ? endDate : formatDateISO(endDate)

      // Single query to demand_forecasts - our unified source of truth
      let query = supabase
        .from('demand_forecasts')
        .select(`
          id,
          forecast_date,
          strain_id,
          strains (
            id,
            name
          ),
          recurring_kg,
          predicted_kg,
          calibrated_kg,
          calibration_factor,
          actual_kg,
          is_actual
        `)
        .gte('forecast_date', start)
        .lte('forecast_date', end)
        .order('forecast_date', { ascending: true })

      // Apply strain filter if provided
      if (strainId) {
        query = query.eq('strain_id', strainId)
      }

      const { data: forecasts, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      // Transform to expected format
      const demandData = (forecasts || []).map(row => ({
        date: row.forecast_date,
        strainId: row.strain_id,
        strainName: row.strains?.name || 'Unknown',
        // Use actual_kg if available (past dates), otherwise calibrated_kg (forecasts)
        demandKg: row.is_actual ? (row.actual_kg || 0) : (row.calibrated_kg || 0),
        isActual: row.is_actual,
        // Additional data for debugging/transparency
        recurringKg: row.recurring_kg || 0,
        predictedKg: row.predicted_kg || 0,
        calibratedKg: row.calibrated_kg || 0,
        calibrationFactor: row.calibration_factor || 1.0
      }))

      // Sort by date then strain name
      demandData.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date)
        if (dateCompare !== 0) return dateCompare
        return a.strainName.localeCompare(b.strainName)
      })

      setData(demandData)
    } catch (err) {
      console.error('[useTimelineDemand] Error:', err)
      setError(err.message)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, strainId])

  useEffect(() => {
    fetchDemand()
  }, [fetchDemand])

  return {
    data,
    loading,
    error,
    refetch: fetchDemand
  }
}

/**
 * Fallback: Fetch demand using legacy method (direct queries)
 * Used when demand_forecasts table hasn't been populated yet
 *
 * @deprecated Use demand_forecasts table via useTimelineDemand instead
 */
export async function fetchLegacyDemand(startDate, endDate, strainId = null) {
  const today = formatDateISO(createDate())

  // Fetch actual sales for past dates
  const actualDemand = await fetchActualSales(startDate, endDate, strainId, today)

  // Fetch projected demand from recurring orders for future dates
  const projectedDemand = await fetchProjectedDemand(startDate, endDate, strainId, today)

  // Merge and deduplicate (actuals take precedence)
  return mergeActualAndProjected(actualDemand, projectedDemand)
}

/**
 * Fetch actual sales from the sales and sale_items tables
 * @deprecated Legacy method - use demand_forecasts instead
 */
async function fetchActualSales(startDate, endDate, strainId, today) {
  const actualEndDate = endDate <= today ? endDate : today

  if (startDate > today) {
    return []
  }

  let query = supabase
    .from('sales')
    .select(`
      id,
      date,
      disregarded,
      sale_items (
        quantity,
        product_id,
        products (
          strain
        )
      )
    `)
    .gte('date', startDate)
    .lte('date', actualEndDate)
    .eq('disregarded', false)

  const { data: sales, error } = await query

  if (error) {
    throw error
  }

  const demandMap = new Map()

  for (const sale of sales) {
    for (const item of sale.sale_items || []) {
      const strainName = item.products?.strain
      if (!strainName) continue

      const key = `${sale.date}|${strainName}`
      const existing = demandMap.get(key) || {
        date: sale.date,
        strainName,
        demandKg: 0,
        isActual: true
      }

      existing.demandKg += parseFloat(item.quantity) || 0
      demandMap.set(key, existing)
    }
  }

  return Array.from(demandMap.values())
}

/**
 * Fetch projected demand from recurring order instances
 * @deprecated Legacy method - use demand_forecasts instead
 */
async function fetchProjectedDemand(startDate, endDate, strainId, today) {
  const projectionStartDate = startDate > today ? startDate : today

  if (endDate <= today) {
    return []
  }

  const [recurringDemand, aiSuggestionsDemand] = await Promise.all([
    fetchRecurringOrderDemand(projectionStartDate, endDate, strainId),
    fetchAISuggestionsDemand(projectionStartDate, endDate, strainId)
  ])

  const demandMap = new Map()

  for (const record of recurringDemand) {
    const key = `${record.date}|${record.strainName}`
    demandMap.set(key, { ...record })
  }

  for (const record of aiSuggestionsDemand) {
    const key = `${record.date}|${record.strainName}`
    const existing = demandMap.get(key)
    if (existing) {
      existing.demandKg += record.demandKg
    } else {
      demandMap.set(key, { ...record })
    }
  }

  return Array.from(demandMap.values())
}

/**
 * Fetch demand from recurring order instances
 * @deprecated Legacy method
 */
async function fetchRecurringOrderDemand(startDate, endDate, strainId) {
  const { data: instances, error: instanceError } = await supabase
    .from('recurring_order_instances')
    .select(`
      id,
      scheduled_date,
      status,
      recurring_order:recurring_orders (
        id,
        recurring_order_items (
          quantity,
          product_id,
          products (
            strain
          )
        )
      )
    `)
    .eq('status', 'pending')
    .gte('scheduled_date', startDate)
    .lte('scheduled_date', endDate)

  if (instanceError) {
    throw instanceError
  }

  const demandMap = new Map()

  for (const instance of instances || []) {
    const order = instance.recurring_order
    if (!order) continue

    for (const item of order.recurring_order_items || []) {
      const strainName = item.products?.strain
      if (!strainName) continue

      const key = `${instance.scheduled_date}|${strainName}`
      const existing = demandMap.get(key) || {
        date: instance.scheduled_date,
        strainName,
        demandKg: 0,
        isActual: false,
        source: 'recurring'
      }

      existing.demandKg += parseFloat(item.quantity) || 0
      demandMap.set(key, existing)
    }
  }

  return Array.from(demandMap.values())
}

/**
 * Fetch demand from AI suggestions
 * @deprecated Legacy method
 */
async function fetchAISuggestionsDemand(startDate, endDate, strainId) {
  const { data: suggestions, error: suggestionsError } = await supabase
    .from('sale_suggestions')
    .select(`
      id,
      suggested_date,
      suggested_quantity,
      confidence_score,
      product:products (
        id,
        strain
      )
    `)
    .eq('status', 'pending')
    .gte('suggested_date', startDate)
    .lte('suggested_date', endDate)

  if (suggestionsError) {
    throw suggestionsError
  }

  const demandMap = new Map()

  for (const suggestion of suggestions || []) {
    const strainName = suggestion.product?.strain
    if (!strainName) continue

    const key = `${suggestion.suggested_date}|${strainName}`
    const existing = demandMap.get(key) || {
      date: suggestion.suggested_date,
      strainName,
      demandKg: 0,
      isActual: false,
      source: 'ai_suggestion'
    }

    existing.demandKg += parseFloat(suggestion.suggested_quantity) || 0
    demandMap.set(key, existing)
  }

  return Array.from(demandMap.values())
}

/**
 * Merge actual and projected demand
 * @deprecated Legacy method
 */
function mergeActualAndProjected(actuals, projected) {
  const actualMap = new Map()
  actuals.forEach(a => {
    actualMap.set(`${a.date}|${a.strainName}`, a)
  })

  const merged = [...actuals]

  projected.forEach(p => {
    const key = `${p.date}|${p.strainName}`
    if (!actualMap.has(key)) {
      merged.push(p)
    }
  })

  merged.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date)
    if (dateCompare !== 0) return dateCompare
    return a.strainName.localeCompare(b.strainName)
  })

  return merged
}

export default useTimelineDemand
